import { useCallback, useSyncExternalStore } from 'react';

export interface TaskNotifications { sound: boolean; popup: boolean }
export type MessageNotificationSource = 'chat' | 'task';
const changed = 'skrepka-task-notifications-change';
const volatile = new Map<string, string>();
const keyFor = (userId?: number) => `skrepka.notifications.tasks.${userId || 'guest'}`;
function snapshot(key: string) {
  if (volatile.has(key)) return volatile.get(key)!;
  try { return localStorage.getItem(key); } catch { return null; }
}
function parse(raw: string | null): TaskNotifications {
  try {
    const value: unknown = JSON.parse(raw || 'null');
    if (value && typeof value === 'object') return {
      sound: !('sound' in value) || typeof value.sound !== 'boolean' ? true : value.sound,
      popup: !('popup' in value) || typeof value.popup !== 'boolean' ? true : value.popup,
    };
  } catch { /* Keep existing notifications enabled for missing or invalid preferences. */ }
  return { sound: true, popup: true };
}
export function readTaskNotifications(userId?: number) { return parse(snapshot(keyFor(userId))); }
export function messageNotificationSource(event: { asset_id?: number }): MessageNotificationSource {
  return typeof event.asset_id === 'number' && event.asset_id > 0 ? 'task' : 'chat';
}
export function useTaskNotifications(userId?: number) {
  const key = keyFor(userId);
  const subscribe = useCallback((notify: () => void) => {
    const storage = (event: StorageEvent) => {
      if (event.key === key || event.key === null) { volatile.delete(key); notify(); }
    };
    window.addEventListener('storage', storage); window.addEventListener(changed, notify);
    return () => { window.removeEventListener('storage', storage); window.removeEventListener(changed, notify); };
  }, [key]);
  const getSnapshot = useCallback(() => snapshot(key), [key]);
  const settings = parse(useSyncExternalStore(subscribe, getSnapshot, () => null));
  const setEnabled = useCallback((channel: keyof TaskNotifications, enabled: boolean) => {
    const raw = JSON.stringify({ ...parse(snapshot(key)), [channel]: enabled });
    try { localStorage.setItem(key, raw); volatile.delete(key); } catch { volatile.set(key, raw); }
    window.dispatchEvent(new Event(changed));
  }, [key]);
  return { ...settings, setEnabled };
}
